import apiClient from './client'
import type {
  Tournament,
  TournamentListItem,
  TourneyId,
  LeagueId,
  CreateTournamentRequest,
  UpdateTournamentRequest,
  ApproveLineupsRequest,
  PreviewFieldRequest,
  PreviewFieldResponse,
  SuccessResponse,
  Lineup,
} from '@/types/api'

export const tournamentsApi = {
  // Get all tournaments
  getTournaments: async (leagueId?: string | LeagueId, status?: string): Promise<TournamentListItem[]> => {
    const response = await apiClient.get<TournamentListItem[]>('/tournaments', {
      params: { leagueId, status },
    })
    return response.data
  },

  // Get tournament by ID
  getTournament: async (tourneyId: TourneyId): Promise<Tournament> => {
    const response = await apiClient.get<Tournament>(`/tournaments/${tourneyId}`)
    return response.data
  },

  // Create tournament (admin)
  createTournament: async (data: CreateTournamentRequest): Promise<{ tourneyId: TourneyId }> => {
    const response = await apiClient.post<{ tourneyId: TourneyId }>('/tournaments', data)
    return response.data
  },

  // Update tournament (admin)
  updateTournament: async (
    tourneyId: TourneyId,
    data: UpdateTournamentRequest
  ): Promise<SuccessResponse> => {
    const response = await apiClient.patch<SuccessResponse>(`/tournaments/${tourneyId}`, data)
    return response.data
  },

  // Approve lineups (admin)
  approveLineups: async (
    tourneyId: TourneyId,
    data: ApproveLineupsRequest
  ): Promise<SuccessResponse> => {
    const response = await apiClient.post<SuccessResponse>(
      `/admin/tournaments/${tourneyId}/approve-lineups`,
      data
    )
    return response.data
  },

  // Get all lineups for a tournament (admin)
  getTournamentLineups: async (tourneyId: TourneyId): Promise<Lineup[]> => {
    const response = await apiClient.get<Lineup[]>(`/tournaments/${tourneyId}/lineups`)
    return response.data
  },

  // Refresh tournament cache (admin)
  refreshCache: async (): Promise<SuccessResponse> => {
    const response = await apiClient.post<SuccessResponse>('/tournaments/refresh-cache', {})
    return response.data
  },

  // Preview tournament field and groups (admin)
  previewField: async (data: PreviewFieldRequest): Promise<PreviewFieldResponse> => {
    const response = await apiClient.post<PreviewFieldResponse>('/admin/tournaments/preview-field', data)
    return response.data
  },

  // Get all daily payout leaderboards for a tournament
  getDailyPayouts: async (tourneyId: TourneyId): Promise<{
    r1?: any[]
    r2?: any[]
    r3?: any[]
    r4?: any[]
  }> => {
    const response = await apiClient.get<{
      r1?: any[]
      r2?: any[]
      r3?: any[]
      r4?: any[]
    }>(`/tournaments/${tourneyId}/daily-payouts?v=2`) // Cache-bust for live daily updates
    return response.data
  },

  // Get snapshot leaderboard for movement tracking
  getSnapshot: async (tourneyId: TourneyId, round: 'r1' | 'r2' | 'r3'): Promise<any[]> => {
    const response = await apiClient.get<any[]>(`/tournaments/${tourneyId}/snapshot/${round}`)
    return response.data
  },

  // Finalize tournament - calculate payouts and mark complete (admin)
  finalizeTournament: async (tourneyId: TourneyId): Promise<{ ok: boolean; payouts: number[] }> => {
    const response = await apiClient.post<{ ok: boolean; payouts: number[] }>(
      `/admin/tournaments/${tourneyId}/finalize`,
      {}
    )
    return response.data
  },

  // Delete single lineup from tournament (admin)
  deleteLineup: async (tourneyId: TourneyId, lineupId: string): Promise<SuccessResponse> => {
    const response = await apiClient.delete<SuccessResponse>(
      `/admin/tournaments/${tourneyId}/lineups/${lineupId}`
    )
    return response.data
  },
}
